import React, { useState } from 'react'
import { Loader2, Package, Barcode, Tag, Layers, Calendar, DollarSign, Boxes, Eye } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { useQuery } from '@tanstack/react-query'
import { productService } from '@/app/services/product.service'
import { ImagePreviewModal } from '../../ui/ImagePreviewModal'

interface DetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number | null
}

function InfoRow({
  label,
  value,
}: {
  label: string
  value?: React.ReactNode
}) {
  return (
    <div className="min-w-0">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </span>
      <p className="break-words mt-0.5 overflow-wrap-anywhere text-sm font-medium text-slate-900">
        {value ?? '—'}
      </p>
    </div>
  )
}

export function DetailModal({ open, onOpenChange, productId }: DetailModalProps) {
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => productService.getProduct(productId!),
    enabled: open && productId !== null,
  })

  const [previewImage, setPreviewImage] = useState<string | null>(null)
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalles del Producto</DialogTitle>
          <DialogDescription className="sr-only">
            Información completa del producto
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="ml-3 text-slate-600">Cargando información...</span>
          </div>
        ) : !product ? (
          <div className="py-12 text-center text-slate-500">
            <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p>No se encontró el producto.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header con imagen y nombre */}
            <div className="flex items-start gap-4">
              {product.hero_image ? (
                <div className="group relative h-20 w-20 shrink-0 cursor-pointer overflow-hidden rounded-lg border border-slate-200 shadow-sm" onClick={() => setPreviewImage(product.hero_image!)}>
                  <img
                    src={product.hero_image}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform group-hover:scale-110"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                </div>
              ) : (
                <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                  <Package className="h-8 w-8 text-slate-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="break-words text-lg font-semibold text-slate-900 truncate">{product.name}</h2>
                {product.description && (
                  <p className="break-words mt-1 text-sm text-slate-500 line-clamp-2">{product.description}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${product.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                    {product.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                  {product.product_type && (
                    <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary">
                      {product.product_type.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Código de barras destacado */}
            {product.barcode && (
              <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <Barcode className="h-5 w-5 shrink-0 text-slate-500" />
                <div>
                  <p className="text-xs text-slate-500">Código de Barras</p>
                  <p className="font-mono text-sm font-semibold text-slate-900">{product.barcode}</p>
                </div>
              </div>
            )}

            {/* Clasificación */}
            <div>
              <div className="mb-2 flex items-center gap-2">
                <Layers className="h-4 w-4 text-slate-400" />
                <h3 className="text-sm font-semibold text-slate-700">Clasificación</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-3">
                <InfoRow label="Categoría" value={product.category?.name} />
                <InfoRow label="Departamento" value={product.department?.name} />
                <InfoRow label="Tipo de Producto" value={product.product_type?.name} />
                <InfoRow label="Unidad de Medida" value={product.unit?.name} />
                {product.sat_product_key && (
                  <InfoRow label="Clave SAT" value={`${product.sat_product_key.code} - ${product.sat_product_key.description}`} />
                )}
              </div>
            </div>

            {/* Precios */}
            {product.price && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Precios</h3>
                </div>
                <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-4">
                  <InfoRow
                    label="Costo s/imp."
                    value={`$${Number(product.price.cost_without_taxes).toFixed(2)}`}
                  />
                  <InfoRow
                    label="Costo c/imp."
                    value={`$${Number(product.price.cost_with_taxes).toFixed(2)}`}
                  />
                  {product.price.sale_price !== undefined && (
                    <InfoRow
                      label="Precio Base"
                      value={`$${Number(product.price.sale_price).toFixed(2)}`}
                    />
                  )}
                  {product.price.final_price !== undefined && (
                    <InfoRow
                      label="Precio de Venta"
                      value={<span className="text-green-700">${Number(product.price.final_price).toFixed(2)}</span>}
                    />
                  )}
                  <InfoRow label="Utilidad" value={`${product.price.profit_percentage}%`} />
                </div>
              </div>
            )}

            {/* Precios por Cantidad */}
            {product.price_tiers && product.price_tiers.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Precios por Cantidad</h3>
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Cant. Mínima</th>
                        <th className="px-4 py-2 text-right font-medium">Utilidad %</th>
                        <th className="px-4 py-2 text-right font-medium">Descuento %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {product.price_tiers.map((tier: any) => (
                        <tr key={tier.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium">{tier.min_quantity}</td>
                          <td className="px-4 py-2 text-right text-slate-500">{tier.profit_percentage}%</td>
                          <td className="px-4 py-2 text-right text-slate-500">{tier.discount_percentage}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Impuestos */}
            {product.taxes && product.taxes.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Tag className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Impuestos aplicados</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.taxes.map((tax) => (
                    <span key={tax.id} className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                      {tax.name} — {tax.percentage}%
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Lotes */}
            {/*
            {product.batches && product.batches.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Lotes activos</h3>
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">No. Lote</th>
                        <th className="px-4 py-2 text-left font-medium">Caducidad</th>
                        <th className="px-4 py-2 text-right font-medium">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {product.batches.map((batch) => (
                        <tr key={batch.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-mono">{batch.batch_number}</td>
                          <td className="px-4 py-2">{batch.expiration_date}</td>
                          <td className="px-4 py-2 text-right font-medium">{batch.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            */}

            {/* Stock por sucursal */}
            {product.stock_by_branch && product.stock_by_branch.length > 0 && (
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <Boxes className="h-4 w-4 text-slate-400" />
                  <h3 className="text-sm font-semibold text-slate-700">Stock por Sucursal</h3>
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Sucursal</th>
                        <th className="px-4 py-2 text-right font-medium">Stock</th>
                        <th className="px-4 py-2 text-right font-medium">Mín.</th>
                        <th className="px-4 py-2 text-right font-medium">Máx.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {product.stock_by_branch.map((branch) => (
                        <tr key={branch.sucursal_id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 font-medium">{branch.sucursal_name}</td>
                          <td className="px-4 py-2 text-right font-semibold">{branch.stock}</td>
                          <td className="px-4 py-2 text-right text-slate-500">{branch.stock_minimo}</td>
                          <td className="px-4 py-2 text-right text-slate-500">{branch.stock_maximo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Galería */}
            {product.images && product.images.length > 0 && (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-slate-700">Galería</h3>
                <div className="flex flex-wrap gap-3">
                  {product.images.map((img) => (
                    <div
                      key={img.id}
                      className="group relative h-20 w-20 cursor-pointer overflow-hidden rounded-lg border border-slate-200 shadow-sm"
                      onClick={() => setPreviewImage(img.image_url)}
                    >
                      <img
                        src={img.image_url}
                        alt="Imagen del producto"
                        className="h-full w-full object-cover transition-transform group-hover:scale-110"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                        <Eye className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>

      <ImagePreviewModal
        open={previewImage !== null}
        onOpenChange={(open) => !open && setPreviewImage(null)}
        imageUrl={previewImage}
      />
    </Dialog>
  )
}
